// TelcoTrack — sazba firmy (investor), sazba zaměstnanců firmy a sazba uživatele.
// `price` ve výkazu je odměna pracovníka. Investor platí sazbu firmy (metadata
// `orgCompanyUnitPrice`); faktura ji dopočte přes `workReportInvestorLineTotal`.

import { getWorkReportPriceListCatalog } from './work-report-price-list.constants';
import { normalizeExcavationPricingMode, type ExcavationPricingMode } from './excavation-material.constants';

export const ORG_COMPANY_UNIT_PRICE_META = 'orgCompanyUnitPrice';
export const ORG_USER_UNIT_PRICE_META = 'orgUserUnitPrice';
export const ORG_MARGIN_PER_UNIT_META = 'orgMarginPerUnit';

/**
 * Rezervovaný klíč v mapě sazeb (`company_work_rates` / `company_employee_work_rates` / `user_work_rates`),
 * který drží výchozí režim ceníku výkopů (`0` = `linear` v m, `1` = `volume` v m³/m²) bez nutnosti DB migrace.
 */
export const ORG_EXCAVATION_PRICING_MODE_RATE_KEY = '__excavation_pricing_mode__';

export function readExcavationPricingModeFromRateMap(
  rates: Record<string, number> | null | undefined,
): ExcavationPricingMode | undefined {
  if (!rates) return undefined;
  const raw = rates[ORG_EXCAVATION_PRICING_MODE_RATE_KEY];
  if (raw == null || !Number.isFinite(Number(raw))) return undefined;
  return Number(raw) >= 1 ? 'volume' : 'linear';
}

export function resolveOrgExcavationPricingMode(
  companyRates?: Record<string, number> | null,
  employeeRates?: Record<string, number> | null,
  userRates?: Record<string, number> | null,
): ExcavationPricingMode {
  return (
    readExcavationPricingModeFromRateMap(userRates) ??
    readExcavationPricingModeFromRateMap(employeeRates) ??
    readExcavationPricingModeFromRateMap(companyRates) ??
    normalizeExcavationPricingMode(undefined)
  );
}

export function stripReservedOrgRateKeys(
  rates: Record<string, number> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, val] of Object.entries(rates ?? {})) {
    if (key === ORG_EXCAVATION_PRICING_MODE_RATE_KEY) continue;
    out[key] = val;
  }
  return out;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function finiteNonNegative(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value) || value < 0) return null;
  return value;
}

/** Globální výchozí ceník výkazu (priceListId → Kč) — stejná čísla jako dřív před ceníkem firmy. */
export function workReportCatalogRateMap(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of getWorkReportPriceListCatalog()) {
    if (!Number.isFinite(row.pricePerUnit) || row.pricePerUnit < 0) continue;
    out[row.priceListId] = row.pricePerUnit;
  }
  return out;
}

/**
 * Strop rozpočtu firmy. Prázdný/chybějící ceník firmy → katalog výkazu (legacy default).
 * Jakmile firma má aspoň jednu sazbu, bere se jen ten ceník (bez doplňování katalogem).
 */
export function companyRatesOrCatalogFallback(
  companyRates: Record<string, number> | null | undefined,
): Record<string, number> {
  const realRates = stripReservedOrgRateKeys(companyRates);
  if (Object.keys(realRates).length > 0) return realRates;
  return workReportCatalogRateMap();
}

/**
 * Jednotková odměna pracovníka. Tahle hodnota jde do `price` ve výkazu (množství × sazba).
 * Investor dál platí sazbu firmy (`companyRates`) — ne tuhle odměnu. Faktura ji bere
 * z `orgCompanyUnitPrice` přes `workReportInvestorLineTotal`, ať `price` zůstane výplata.
 * Prázdná sazba zaměstnanců firmy = odměna je celá sazba firmy (žádná marže).
 * Sazba uživatele smí odměnu jen snížit: strop je sazba zaměstnanců, pokud je nastavená,
 * jinak sazba firmy.
 * Prázdný ceník firmy, nebo položka v ceníku firmy chybí: strop je katalog výkazu,
 * ale jen když existuje sazba zaměstnanců nebo uživatele (včetně 0).
 * Bez těch sazeb klíč v mapě není — platí katalog / polygon jako dřív.
 */
export function resolveOrgPayoutRates(
  companyRates: Record<string, number> | null | undefined,
  userRates: Record<string, number> | null | undefined,
  employeeRates?: Record<string, number> | null,
): Record<string, number> {
  const out: Record<string, number> = {};
  const stored = stripReservedOrgRateKeys(companyRates);
  const cleanUser = stripReservedOrgRateKeys(userRates);
  const cleanEmployee = stripReservedOrgRateKeys(employeeRates);
  const catalog = workReportCatalogRateMap();
  if (Object.keys(stored).length > 0) {
    for (const [key, rawCeiling] of Object.entries(stored)) {
      const ceiling = finiteNonNegative(rawCeiling);
      if (ceiling == null) continue;
      out[key] = workerUnitPrice(ceiling, cleanEmployee[key], cleanUser[key]);
    }
  }
  const personalKeys = new Set([...Object.keys(cleanUser), ...Object.keys(cleanEmployee)]);
  for (const key of personalKeys) {
    if (Object.prototype.hasOwnProperty.call(out, key)) continue;
    const ceiling = finiteNonNegative(catalog[key]);
    if (ceiling == null) continue;
    out[key] = workerUnitPrice(ceiling, cleanEmployee[key], cleanUser[key]);
  }
  return out;
}

/**
 * Metadata řádku. `orgCompanyUnitPrice` je částka investora za jednotku.
 * `orgUserUnitPrice` je skutečná odměna pracovníka, jen když ji srazila sazba
 * zaměstnanců nebo uživatele. Marže firmy je rozdíl a není záporná.
 * Když položka v ceníku firmy chybí, stropem je katalog výkazu — ale jen když
 * existuje sazba zaměstnanců nebo uživatele (včetně 0). Jinak null = katalog / polygon.
 */
export function orgRateLineMetadata(
  priceListKey: string,
  companyRates: Record<string, number> | null | undefined,
  userRates: Record<string, number> | null | undefined,
  employeeRates?: Record<string, number> | null,
): Record<string, number> | null {
  const key = priceListKey.trim();
  if (!key || key === ORG_EXCAVATION_PRICING_MODE_RATE_KEY) return null;
  const stored = stripReservedOrgRateKeys(companyRates);
  const cleanUser = stripReservedOrgRateKeys(userRates);
  const cleanEmployee = stripReservedOrgRateKeys(employeeRates);
  const employee = finiteNonNegative(cleanEmployee[key]);
  const user = finiteNonNegative(cleanUser[key]);
  let ceiling = finiteNonNegative(stored[key]);
  if (ceiling == null && (employee != null || user != null)) {
    ceiling = finiteNonNegative(workReportCatalogRateMap()[key]);
  }
  if (ceiling == null) return null;
  const payout = workerUnitPrice(ceiling, employee, user);
  const meta: Record<string, number> = {
    [ORG_COMPANY_UNIT_PRICE_META]: roundMoney(ceiling),
    [ORG_MARGIN_PER_UNIT_META]: roundMoney(Math.max(0, ceiling - payout)),
  };
  if (employee != null || user != null) {
    meta[ORG_USER_UNIT_PRICE_META] = roundMoney(payout);
  }
  return meta;
}

/**
 * Částka řádku, kterou platí investor. `price` je odměna pracovníka.
 *
 * - Sazba pracovníka > 0 a obě sazby v metadatech:
 *   `price × orgCompanyUnitPrice / orgUserUnitPrice`
 *   (historický poměr, i když množství chybí — podíl spolupráce už je v `price`).
 * - Sazba pracovníka = 0 a sazba firmy > 0:
 *   `quantity × orgCompanyUnitPrice / workShareCount`.
 *   Množství je `metadata.quantity`, jinak sloupec `excavationLengthM`,
 *   když jednotka je `m` nebo na řádku není. `workShareCount` < 2 se bere jako 1.
 *   Množství na sdíleném řádku je celé a `price` je 0, takže podíl z ceny nejde.
 * - Bez sazby pracovníka (ruční Ostatní, nebo odměna = sazba firmy): `price`.
 */
export function workReportInvestorLineTotal(
  price: number | null | undefined,
  metadata: Record<string, unknown> | null | undefined,
  columnQuantity?: number | string | null,
): number {
  const line = Number(price);
  if (!Number.isFinite(line)) return 0;
  const company = metaMoney(metadata, ORG_COMPANY_UNIT_PRICE_META);
  const worker = metaMoney(metadata, ORG_USER_UNIT_PRICE_META);
  if (worker === 0 && company != null && company > 0) {
    const qty = workReportBillableQuantity(metadata, columnQuantity);
    if (qty != null) {
      return roundMoney((qty * company) / workShareDivisor(metadata));
    }
  }
  if (company == null || worker == null || worker <= 0) return roundMoney(line);
  return roundMoney(line * (company / worker));
}

/**
 * Opraví cenu, kterou klient spočítal z katalogu nebo ze sazby firmy, na odměnu pracovníka.
 * Sazba uživatele 0 nechá výplatu 0. Ruční částka, která se neshoduje s katalogem ani se
 * sazbou firmy, zůstane (admin / Ostatní). `null` = cenu neměnit.
 */
export function correctSubmittedWorkerPrice(input: {
  quantity: number | null | undefined;
  userUnitPrice: number | null | undefined;
  companyUnitPrice?: number | null;
  catalogUnitPrice?: number | null;
  clientPrice?: number | null;
  amountMode?: boolean;
}): number | null {
  if (input.amountMode) return null;
  const user = finiteNonNegative(input.userUnitPrice ?? undefined);
  if (user == null) return null;
  const qtyRaw = input.quantity;
  const qty = qtyRaw != null && Number.isFinite(qtyRaw) ? Math.abs(qtyRaw) : null;
  const client =
    input.clientPrice != null && Number.isFinite(input.clientPrice) ? roundMoney(input.clientPrice) : null;
  const substitutes = new Set<number>();
  const addUnit = (unit: number | null): void => {
    if (unit == null) return;
    substitutes.add(roundMoney(unit));
    if (qty != null && qty > 0) substitutes.add(roundMoney(qty * unit));
  };
  addUnit(finiteNonNegative(input.companyUnitPrice ?? undefined));
  addUnit(finiteNonNegative(input.catalogUnitPrice ?? undefined));
  if (user === 0) {
    if (client == null || client === 0 || substitutes.has(client)) return 0;
    return null;
  }
  if (qty == null || qty === 0) return null;
  const expected = roundMoney(qty * user);
  if (client == null || client === expected) return expected;
  substitutes.add(roundMoney(user));
  return substitutes.has(client) ? expected : null;
}

/**
 * Katalogová sazba pracovníka je výslovně 0 a firma má za jednotku kladnou smluvní cenu.
 * Výplata řádku je pak 0; částka investora se dopočte z množství, ne z `price`.
 */
export function isZeroWorkerRateCompanyContract(
  metadata: object | null | undefined,
): boolean {
  const company = metaMoney(metadata as Record<string, unknown> | null | undefined, ORG_COMPANY_UNIT_PRICE_META);
  const worker = metaMoney(metadata as Record<string, unknown> | null | undefined, ORG_USER_UNIT_PRICE_META);
  return worker === 0 && company != null && company > 0;
}

export type WorkReportMoneyView = 'worker' | 'company';

/** Výkaz práce = odměna (`price`). Výdělek firmy = smluvní řádek investora. */
export function workReportLineAmount(
  price: number | null | undefined,
  metadata: Record<string, unknown> | null | undefined,
  view: WorkReportMoneyView = 'worker',
  columnQuantity?: number | string | null,
): number {
  const line = Number(price);
  const payout = Number.isFinite(line) ? line : 0;
  if (view === 'company') return workReportInvestorLineTotal(payout, metadata, columnQuantity);
  return payout;
}

/** Součet faktury ve Fakturaci je `company` (smluvní), ne odměna pracovníka. */
export function sumWorkReportLineAmounts(
  rows: readonly {
    price?: number | null;
    metadata?: Record<string, unknown> | null;
    excavationLengthM?: number | string | null;
  }[],
  view: WorkReportMoneyView,
): number {
  const total = rows.reduce(
    (sum, row) =>
      sum + workReportLineAmount(row.price, row.metadata ?? null, view, row.excavationLengthM),
    0,
  );
  return roundMoney(total);
}

function workerUnitPrice(
  ceiling: number,
  employeeRate: number | null | undefined,
  userRate: number | null | undefined,
): number {
  const employee = finiteNonNegative(employeeRate);
  const orgPay = employee == null ? ceiling : Math.min(employee, ceiling);
  const user = finiteNonNegative(userRate);
  const payout = user == null ? orgPay : Math.min(user, orgPay);
  return roundMoney(payout);
}

function metaMoney(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  if (!metadata) return null;
  return finiteNonNegative(positiveOrZeroNumber(metadata[key]));
}

/** Kladné množství. `0` a záporné hodnoty vrací null. */
function positiveQuantity(raw: unknown): number | null {
  const n = positiveOrZeroNumber(raw);
  if (n == null || n <= 0) return null;
  return n;
}

function positiveOrZeroNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return finiteNonNegative(Number.isFinite(n) ? n : undefined);
}

/**
 * Účtované množství řádku. Nejdřív `metadata.quantity`.
 * Když množství v metadatech není, sloupec `excavation_length_m`
 * (jen jednotka `m`, nebo když jednotka na řádku chybí).
 */
function workReportBillableQuantity(
  metadata: Record<string, unknown> | null | undefined,
  columnQuantity?: number | string | null,
): number | null {
  const rawQty = metadata?.['quantity'];
  const qtyPresent = rawQty != null && rawQty !== '';
  if (qtyPresent) return positiveQuantity(rawQty);
  const unitRaw = metadata?.['unit'];
  const unit = typeof unitRaw === 'string' ? unitRaw.trim().toLowerCase() : '';
  if (unit !== '' && unit !== 'm') return null;
  return positiveQuantity(columnQuantity);
}

/** Plné množství je na každém podílu spolupráce; dělitel je počet osob. */
function workShareDivisor(metadata: Record<string, unknown> | null | undefined): number {
  const parts = positiveQuantity(metadata?.['workShareCount']);
  if (parts == null || parts < 2) return 1;
  return parts;
}
